import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Divider, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCobro, getMesa } from "../api/cobroCuenta";
import PoweredByTipify from "../components/PoweredByTipify";
import { event } from "../utils/gtag";

/**
 * Pantalla tras pagar la cuenta de la mesa: tiquet con la cuenta y la
 * propina, y si la mesa ya se cerró en la caja del restaurante.
 */
export default function CuentaMesaOkScreen() {
  const { codigo, cobroId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [cobro, setCobro] = useState(null);
  const [qrId, setQrId] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let vivo = true;
    let intentos = 0;
    let timer = null;
    // El pago y el cierre en caja se confirman por webhook: se consulta unos segundos
    const consultar = async () => {
      intentos += 1;
      try {
        const { data } = await getCobro(cobroId);
        if (!vivo) return;
        setCobro(data);
        const listo = data.pagado && data.cierre && data.cierre !== "pendiente";
        if (data.pagado && intentos === 1) event({ action: "cargar_pantalla_exito", category: "Éxito", label: "CuentaMesa" });
        if (!listo && intentos < 20) timer = setTimeout(consultar, 1500);
      } catch {
        if (vivo) setError(true);
      }
    };
    consultar();
    getMesa(codigo)
      .then(({ data }) => vivo && setQrId(data.qrId))
      .catch(() => {});
    return () => {
      vivo = false;
      clearTimeout(timer);
    };
  }, [codigo, cobroId]);

  const dinero = (cents) =>
    new Intl.NumberFormat(i18n.language || "es", { style: "currency", currency: String(cobro?.currency || "eur").toUpperCase() }).format(
      Number(cents || 0) / 100,
    );

  const fila = (label, cents, fuerte = false) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.25 }}>
      <Typography sx={{ fontWeight: fuerte ? 700 : 400 }}>{label}</Typography>
      <Typography sx={{ fontWeight: fuerte ? 700 : 400 }}>{dinero(cents)}</Typography>
    </Box>
  );

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: "#fff", px: { xs: 2.5, sm: 4 }, py: 4, maxWidth: 560, mx: "auto" }}>
      {error && <Alert severity="error">{t("cuentaMesa.errorPago")}</Alert>}
      {cobro && (
        <>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <CheckCircleRoundedIcon sx={{ fontSize: 64, color: cobro.pagado ? "#2e7d32" : "#bbb" }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {cobro.pagado ? t("cuentaMesa.okTitulo") : t("cuentaMesa.okProcesando")}
            </Typography>
            <Typography sx={{ color: "#666" }}>{t("cuentaMesa.mesa", { nombre: cobro.mesa?.nombre })}</Typography>
          </Box>

          <Box sx={{ border: "1px solid #eee", borderRadius: 2, p: 2, mb: 2 }}>
            {(cobro.lineas || []).map((l, i) => (
              <Box key={i} sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.25 }}>
                <Typography variant="body2" sx={{ color: "#555" }}>
                  {l.cantidad} × {l.nombre}
                </Typography>
                <Typography variant="body2" sx={{ color: "#555" }}>
                  {dinero(l.totalCents)}
                </Typography>
              </Box>
            ))}
            <Divider sx={{ my: 1 }} />
            {fila(t("cuentaMesa.resumenCuenta"), cobro.amounts.cuentaCents)}
            {cobro.amounts.tipCents > 0 && fila(t("cuentaMesa.resumenPropina"), cobro.amounts.tipCents)}
            {cobro.amounts.feeCents > 0 && fila(t("cuentaMesa.resumenGastos"), cobro.amounts.feeCents)}
            <Divider sx={{ my: 1 }} />
            {fila(t("cuentaMesa.resumenTotal"), cobro.amounts.totalCents, true)}
          </Box>

          {cobro.pagado && (
            <Alert severity={cobro.cierre === "hecho" ? "success" : "info"} sx={{ mb: 2 }}>
              {cobro.cierre === "hecho" ? t("cuentaMesa.mesaCerrada") : t("cuentaMesa.mesaPendiente")}
            </Alert>
          )}

          {cobro.pagado && cobro.tipId && qrId && (
            <Button
              fullWidth
              variant="outlined"
              sx={{ textTransform: "none", borderRadius: 999, py: 1.25 }}
              onClick={() => navigate(`/${qrId}/success/${cobro.tipId}?method=stripe`)}
            >
              {t("cuentaMesa.valorar")}
            </Button>
          )}
        </>
      )}
      <PoweredByTipify />
    </Box>
  );
}
